import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Print as PrintIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  RemoveRedEye as DilateIcon,
} from '@mui/icons-material';

const OptometryWorkupForm = ({ patient, formData, setFormData, onPrint, onDilate, onViewHistory }) => {
  const handleChange = (section, field, value, subfield = null) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (subfield) {
        if (!newData[section]) newData[section] = {};
        if (!newData[section][field]) newData[section][field] = {};
        newData[section][field][subfield] = value;
      } else if (field) {
        if (!newData[section]) newData[section] = {};
        newData[section][field] = value;
      } else {
        newData[section] = value;
      }
      return newData;
    });
  };

  return (
    <Box sx={{ maxHeight: '70vh', overflowY: 'auto', p: 2 }}>
      {/* Patient Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#e0e0e0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2"><strong>Patient:</strong> {patient?.name} | <strong>Age:</strong> {patient?.age} | <strong>Sex:</strong> {patient?.gender} | <strong>CR No:</strong> {patient?.patientId}</Typography>
          </Grid>
          <Grid item xs={4} md={2}>
            <TextField size="small" label="Visit No." type="number" value={formData.visitNumber || ''} onChange={(e) => handleChange('visitNumber', null, e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={4} md={2}>
            <TextField size="small" label="Optometrist" value={formData.optometrist || ''} onChange={(e) => handleChange('optometrist', null, e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={4} md={2}>
            <TextField size="small" label="Paediatric Ass." value={formData.paediatricAssistant || ''} onChange={(e) => handleChange('paediatricAssistant', null, e.target.value)} fullWidth />
          </Grid>
        </Grid>
      </Paper>

      {/* Systemic History */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Systemic History & Vitals</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Checkbox checked={formData.systemicHistory?.hypertension?.present || false} onChange={(e) => handleChange('systemicHistory', 'hypertension', { ...formData.systemicHistory?.hypertension, present: e.target.checked })} />} label="Hypertension" />
              <TextField size="small" label="Since" value={formData.systemicHistory?.hypertension?.since || ''} onChange={(e) => handleChange('systemicHistory', 'hypertension', { ...formData.systemicHistory?.hypertension, since: e.target.value })} sx={{ ml: 1, width: '100px' }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Checkbox checked={formData.systemicHistory?.diabetes?.present || false} onChange={(e) => handleChange('systemicHistory', 'diabetes', { ...formData.systemicHistory?.diabetes, present: e.target.checked })} />} label="Diabetes" />
              <TextField size="small" label="Since" value={formData.systemicHistory?.diabetes?.since || ''} onChange={(e) => handleChange('systemicHistory', 'diabetes', { ...formData.systemicHistory?.diabetes, since: e.target.value })} sx={{ ml: 1, width: '100px' }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Checkbox checked={formData.systemicHistory?.thyroid?.present || false} onChange={(e) => handleChange('systemicHistory', 'thyroid', { ...formData.systemicHistory?.thyroid, present: e.target.checked })} />} label="Thyroid" />
              <TextField size="small" label="Since" value={formData.systemicHistory?.thyroid?.since || ''} onChange={(e) => handleChange('systemicHistory', 'thyroid', { ...formData.systemicHistory?.thyroid, since: e.target.value })} sx={{ ml: 1, width: '100px' }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Checkbox checked={formData.systemicHistory?.cardiacProblem || false} onChange={(e) => handleChange('systemicHistory', 'cardiacProblem', e.target.checked)} />} label="Cardiac Problem" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Others" value={formData.systemicHistory?.others || ''} onChange={(e) => handleChange('systemicHistory', 'others', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Allergy / Sensitivity" value={formData.systemicHistory?.allergySensitivity || ''} onChange={(e) => handleChange('systemicHistory', 'allergySensitivity', e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField size="small" label="BP Systolic" value={formData.vitals?.bloodPressure?.systolic || ''} onChange={(e) => handleChange('vitals', 'bloodPressure', { ...formData.vitals?.bloodPressure, systolic: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField size="small" label="BP Diastolic" value={formData.vitals?.bloodPressure?.diastolic || ''} onChange={(e) => handleChange('vitals', 'bloodPressure', { ...formData.vitals?.bloodPressure, diastolic: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField size="small" label="Blood Sugar" value={formData.vitals?.bloodSugar || ''} onChange={(e) => handleChange('vitals', 'bloodSugar', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField size="small" label="Weight" value={formData.vitals?.bodyWeight || ''} onChange={(e) => handleChange('vitals', 'bodyWeight', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField size="small" label="ADR+" value={formData.vitals?.adrPlus || ''} onChange={(e) => handleChange('vitals', 'adrPlus', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Ocular History" value={formData.ocularHistory || ''} onChange={(e) => handleChange('ocularHistory', null, e.target.value)} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Visual Acuity */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Visual Acuity</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Eye</strong></TableCell>
                  <TableCell><strong>UCVA</strong></TableCell>
                  <TableCell><strong>VACGL</strong></TableCell>
                  <TableCell><strong>CPH</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Right</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.visualAcuity?.right?.ucva || ''} onChange={(e) => handleChange('visualAcuity', 'right', e.target.value, 'ucva')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.visualAcuity?.right?.vacgl || ''} onChange={(e) => handleChange('visualAcuity', 'right', e.target.value, 'vacgl')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.visualAcuity?.right?.cph || ''} onChange={(e) => handleChange('visualAcuity', 'right', e.target.value, 'cph')} fullWidth /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Left</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.visualAcuity?.left?.ucva || ''} onChange={(e) => handleChange('visualAcuity', 'left', e.target.value, 'ucva')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.visualAcuity?.left?.vacgl || ''} onChange={(e) => handleChange('visualAcuity', 'left', e.target.value, 'vacgl')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.visualAcuity?.left?.cph || ''} onChange={(e) => handleChange('visualAcuity', 'left', e.target.value, 'cph')} fullWidth /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField size="small" label="Amsler RE" value={formData.visualAcuity?.amslerChart?.right || ''} onChange={(e) => handleChange('visualAcuity', 'amslerChart', { ...formData.visualAcuity?.amslerChart, right: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={4}>
              <TextField size="small" label="Amsler LE" value={formData.visualAcuity?.amslerChart?.left || ''} onChange={(e) => handleChange('visualAcuity', 'amslerChart', { ...formData.visualAcuity?.amslerChart, left: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={4}>
              <TextField size="small" label="Color Vision" value={formData.visualAcuity?.colorVision || ''} onChange={(e) => handleChange('visualAcuity', 'colorVision', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Power of Own Glasses */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Power of Own Glasses</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Eye</strong></TableCell>
                  <TableCell><strong>Sph</strong></TableCell>
                  <TableCell><strong>Cyl</strong></TableCell>
                  <TableCell><strong>Axis</strong></TableCell>
                  <TableCell><strong>VA</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Right</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.right?.sphere || ''} onChange={(e) => handleChange('ownGlasses', 'right', e.target.value, 'sphere')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.right?.cylinder || ''} onChange={(e) => handleChange('ownGlasses', 'right', e.target.value, 'cylinder')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.right?.axis || ''} onChange={(e) => handleChange('ownGlasses', 'right', e.target.value, 'axis')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.right?.va || ''} onChange={(e) => handleChange('ownGlasses', 'right', e.target.value, 'va')} fullWidth /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Left</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.left?.sphere || ''} onChange={(e) => handleChange('ownGlasses', 'left', e.target.value, 'sphere')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.left?.cylinder || ''} onChange={(e) => handleChange('ownGlasses', 'left', e.target.value, 'cylinder')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.left?.axis || ''} onChange={(e) => handleChange('ownGlasses', 'left', e.target.value, 'axis')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.ownGlasses?.left?.va || ''} onChange={(e) => handleChange('ownGlasses', 'left', e.target.value, 'va')} fullWidth /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Auto Refractor & CDR */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Auto Refractor & CDR</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Auto Refractor</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Eye</strong></TableCell>
                  <TableCell><strong>Sph</strong></TableCell>
                  <TableCell><strong>Cyl</strong></TableCell>
                  <TableCell><strong>Axis</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Right</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.autoRefractor?.right?.sphere || ''} onChange={(e) => handleChange('autoRefractor', 'right', e.target.value, 'sphere')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.autoRefractor?.right?.cylinder || ''} onChange={(e) => handleChange('autoRefractor', 'right', e.target.value, 'cylinder')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.autoRefractor?.right?.axis || ''} onChange={(e) => handleChange('autoRefractor', 'right', e.target.value, 'axis')} fullWidth /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Left</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.autoRefractor?.left?.sphere || ''} onChange={(e) => handleChange('autoRefractor', 'left', e.target.value, 'sphere')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.autoRefractor?.left?.cylinder || ''} onChange={(e) => handleChange('autoRefractor', 'left', e.target.value, 'cylinder')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.autoRefractor?.left?.axis || ''} onChange={(e) => handleChange('autoRefractor', 'left', e.target.value, 'axis')} fullWidth /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>CDR (Cup to Disc Ratio)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField size="small" label="Right Eye" value={formData.cdr?.right || ''} onChange={(e) => handleChange('cdr', 'right', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Left Eye" value={formData.cdr?.left || ''} onChange={(e) => handleChange('cdr', 'left', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Refraction */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Refraction</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Eye</strong></TableCell>
                  <TableCell><strong>Distance</strong></TableCell>
                  <TableCell><strong>Near Add</strong></TableCell>
                  <TableCell><strong>Near Prescription</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Right</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.refraction?.right?.distance || ''} onChange={(e) => handleChange('refraction', 'right', e.target.value, 'distance')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.refraction?.right?.nearAdd || ''} onChange={(e) => handleChange('refraction', 'right', e.target.value, 'nearAdd')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.refraction?.right?.nearPrescription || ''} onChange={(e) => handleChange('refraction', 'right', e.target.value, 'nearPrescription')} fullWidth /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Left</strong></TableCell>
                  <TableCell><TextField size="small" value={formData.refraction?.left?.distance || ''} onChange={(e) => handleChange('refraction', 'left', e.target.value, 'distance')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.refraction?.left?.nearAdd || ''} onChange={(e) => handleChange('refraction', 'left', e.target.value, 'nearAdd')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.refraction?.left?.nearPrescription || ''} onChange={(e) => handleChange('refraction', 'left', e.target.value, 'nearPrescription')} fullWidth /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Recommendations" value={formData.recommendations || ''} onChange={(e) => handleChange('recommendations', null, e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Optometrist Remarks" value={formData.optometristRemarks || ''} onChange={(e) => handleChange('optometristRemarks', null, e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel control={<Checkbox checked={formData.refraction?.sendSMS || false} onChange={(e) => handleChange('refraction', 'sendSMS', e.target.checked)} />} label="Send SMS" />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Technician" value={formData.refraction?.technician || ''} onChange={(e) => handleChange('refraction', 'technician', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Corrected IOP */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Corrected IOP</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Field</strong></TableCell>
                  <TableCell><strong>RE</strong></TableCell>
                  <TableCell><strong>LE</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Corneal Thickness</TableCell>
                  <TableCell><TextField size="small" value={formData.correctedIOP?.right?.cornealThickness || ''} onChange={(e) => handleChange('correctedIOP', 'right', e.target.value, 'cornealThickness')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.correctedIOP?.left?.cornealThickness || ''} onChange={(e) => handleChange('correctedIOP', 'left', e.target.value, 'cornealThickness')} fullWidth /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Measured IOP</TableCell>
                  <TableCell><TextField size="small" value={formData.correctedIOP?.right?.measuredIOP || ''} onChange={(e) => handleChange('correctedIOP', 'right', e.target.value, 'measuredIOP')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.correctedIOP?.left?.measuredIOP || ''} onChange={(e) => handleChange('correctedIOP', 'left', e.target.value, 'measuredIOP')} fullWidth /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Dilated IOP</TableCell>
                  <TableCell><TextField size="small" value={formData.correctedIOP?.right?.dilatedIOP || ''} onChange={(e) => handleChange('correctedIOP', 'right', e.target.value, 'dilatedIOP')} fullWidth /></TableCell>
                  <TableCell><TextField size="small" value={formData.correctedIOP?.left?.dilatedIOP || ''} onChange={(e) => handleChange('correctedIOP', 'left', e.target.value, 'dilatedIOP')} fullWidth /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Biometry Type</FormLabel>
              <RadioGroup row value={formData.correctedIOP?.biometryType || 'optical'} onChange={(e) => handleChange('correctedIOP', 'biometryType', e.target.value)}>
                <FormControlLabel value="optical" control={<Radio />} label="Optical Biometry" />
                <FormControlLabel value="ultrasonic" control={<Radio />} label="Ultra-Sonic Biometry" />
              </RadioGroup>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Optical Biometry */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">Optical Biometry</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Field</strong></TableCell>
                  <TableCell><strong>RE</strong></TableCell>
                  <TableCell><strong>LE</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {['k1Flat', 'k2Steep', 'kDifference', 'axisK1', 'axisK2', 'axialWW', 'acd', 'ltLens', 'aConst', 'actualACD', 'iolPower', 'iolType'].map((field) => (
                  <TableRow key={field}>
                    <TableCell>{field.replace(/([A-Z])/g, ' $1').trim()}</TableCell>
                    <TableCell><TextField size="small" value={formData.opticalBiometry?.right?.[field] || ''} onChange={(e) => handleChange('opticalBiometry', 'right', e.target.value, field)} fullWidth /></TableCell>
                    <TableCell><TextField size="small" value={formData.opticalBiometry?.left?.[field] || ''} onChange={(e) => handleChange('opticalBiometry', 'left', e.target.value, field)} fullWidth /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Biometry Stage" value={formData.opticalBiometry?.biometryStage || ''} onChange={(e) => handleChange('opticalBiometry', 'biometryStage', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Biometry Done By" value={formData.opticalBiometry?.biometryDoneBy || ''} onChange={(e) => handleChange('opticalBiometry', 'biometryDoneBy', e.target.value)} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons Section */}
      <Paper elevation={3} sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom align="center">
          Actions
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => onPrint && onPrint('blank')}
              sx={{ py: 1.5 }}
            >
              Print Blank
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={() => onPrint && onPrint('filled')}
              sx={{ py: 1.5 }}
            >
              Print
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              startIcon={<DilateIcon />}
              onClick={() => onDilate && onDilate()}
              sx={{ py: 1.5 }}
            >
              DILATE
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<HistoryIcon />}
              onClick={() => onViewHistory && onViewHistory('lens')}
              sx={{ py: 1.5 }}
            >
              Lens History
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<HistoryIcon />}
              onClick={() => onViewHistory && onViewHistory('patient')}
              sx={{ py: 1.5 }}
            >
              Patient History
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewHistory && onViewHistory('user')}
              sx={{ py: 1.5 }}
            >
              User History
            </Button>
          </Grid>
        </Grid>

        {/* Power Formula Selection */}
        <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="center" alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Power Formula 1</InputLabel>
              <Select
                value={formData.opticalBiometry?.powerFormula1 || 'SRK-T'}
                onChange={(e) => handleChange('opticalBiometry', 'powerFormula1', e.target.value)}
                label="Power Formula 1"
              >
                <MenuItem value="SRK-T">SRK-T</MenuItem>
                <MenuItem value="Hoffer Q">Hoffer Q</MenuItem>
                <MenuItem value="Holladay 1">Holladay 1</MenuItem>
                <MenuItem value="Haigis">Haigis</MenuItem>
                <MenuItem value="Barrett Universal II">Barrett Universal II</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Power Formula 2</InputLabel>
              <Select
                value={formData.opticalBiometry?.powerFormula2 || 'SRK-T'}
                onChange={(e) => handleChange('opticalBiometry', 'powerFormula2', e.target.value)}
                label="Power Formula 2"
              >
                <MenuItem value="SRK-T">SRK-T</MenuItem>
                <MenuItem value="Hoffer Q">Hoffer Q</MenuItem>
                <MenuItem value="Holladay 1">Holladay 1</MenuItem>
                <MenuItem value="Haigis">Haigis</MenuItem>
                <MenuItem value="Barrett Universal II">Barrett Universal II</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Footer Info */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Date: {new Date().toLocaleDateString()} | Time: {new Date().toLocaleTimeString()} | Language: ENG
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default OptometryWorkupForm;
